import { useState, useCallback, useRef, useEffect } from 'react';
import { useQueryClient } from '../lib/queryClient';
import { postChat } from './chatApiClient';
import { SearchResult } from '../components/WebSearchResults';

export interface QueuedMessage {
  id: string;
  content: string;
  fileContext: string[];
  attachment?: { content: string; name: string; addToProject?: boolean };
  webSearchResults?: SearchResult[];
  status: 'pending' | 'processing' | 'completed' | 'failed';
  response?: string;
  error?: string;
  createdAt: Date;
  startedAt?: Date;
  completedAt?: Date;
}

export interface StreamingState {
  messageId: string;
  content: string;
  isStreaming: boolean;
}

interface UseAiMessageQueueOptions {
  sessionId: string;
  maxConcurrent?: number; // Max concurrent processing (default 1 for sequential, set higher for parallel)
  autoExecute?: boolean;
  mode?: 'generate' | 'chat';
  selectedModel?: { provider: string; model: string; isLocal: boolean };
  autonomousMode?: boolean; // Enable AI participants to respond
  activeSkills?: Array<{ name: string; content?: string; description?: string }>; // Skills injected into system prompt
  onStreamUpdate?: (messageId: string, content: string) => void;
  onMessageComplete?: (messageId: string, response: string) => void;
  onMessageError?: (messageId: string, error: string) => void;
}

/**
 * Hook for managing a non-blocking AI message queue.
 * Allows users to queue multiple messages while AI is generating.
 * Similar to Claude Code's non-blocking chat behavior.
 */
export function useAiMessageQueue({
  sessionId,
  maxConcurrent = 2, // Allow 2 concurrent by default for responsiveness
  autoExecute = true,
  mode = 'generate',
  selectedModel,
  autonomousMode = false,
  activeSkills,
  onStreamUpdate,
  onMessageComplete,
  onMessageError,
}: UseAiMessageQueueOptions) {
  const queryClient = useQueryClient();
  const [queue, setQueue] = useState<QueuedMessage[]>([]);
  const [activeStreams, setActiveStreams] = useState<Map<string, StreamingState>>(new Map());
  const processingRef = useRef<Set<string>>(new Set());
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  // Count currently processing messages
  const processingCount = queue.filter((m) => m.status === 'processing').length;
  const pendingCount = queue.filter((m) => m.status === 'pending').length;
  const hasActiveWork = processingCount > 0 || pendingCount > 0;

  // Add a message to the queue
  const enqueueMessage = useCallback(
    (
      content: string,
      fileContext: string[] = [],
      attachment?: { content: string; name: string; addToProject?: boolean },
      webSearchResults?: SearchResult[],
    ): string => {
      const messageId = `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      const newMessage: QueuedMessage = {
        id: messageId,
        content,
        fileContext,
        attachment,
        webSearchResults,
        status: 'pending',
        createdAt: new Date(),
      };

      setQueue((prev) => [...prev, newMessage]);
      return messageId;
    },
    [],
  );

  // Process a single message
  const processMessage = useCallback(
    async (message: QueuedMessage) => {
      if (processingRef.current.has(message.id)) return;
      processingRef.current.add(message.id);

      const abortController = new AbortController();
      abortControllersRef.current.set(message.id, abortController);

      // Update status to processing
      setQueue((prev) =>
        prev.map((m) =>
          m.id === message.id ? { ...m, status: 'processing' as const, startedAt: new Date() } : m,
        ),
      );

      // Initialize streaming state
      setActiveStreams((prev) => {
        const newMap = new Map(prev);
        newMap.set(message.id, { messageId: message.id, content: '', isStreaming: true });
        return newMap;
      });

      try {
        // Get the template from localStorage (set when session was created)
        let template: string | undefined;
        try {
          const storedTemplate = localStorage.getItem(`session_template_${sessionId}`);
          if (storedTemplate) template = storedTemplate;
        } catch (_e) {
          /* ignore */
        }

        const response = await postChat(
          {
            sessionId,
            message: message.content,
            fileContext: message.fileContext,
            imageAttachment: message.attachment,
            useLocalLlm: selectedModel?.isLocal,
            provider: (selectedModel?.provider || 'ollama_cloud') as any,
            model: selectedModel?.model || 'qwen3-coder-next:cloud',
            mode,
            autonomousMode,
            autoLoop: mode === 'generate',
            backgroundMode: false,
            template: template as any,
            skills: activeSkills && activeSkills.length > 0 ? activeSkills : undefined,
          },
          {
            signal: abortController.signal,
          },
        );

        // Handle streaming response - server sends raw text chunks (not SSE)
        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let accumulatedContent = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          accumulatedContent += chunk;

          // Update streaming state
          setActiveStreams((prev) => {
            const newMap = new Map(prev);
            newMap.set(message.id, {
              messageId: message.id,
              content: accumulatedContent,
              isStreaming: true,
            });
            return newMap;
          });

          onStreamUpdate?.(message.id, accumulatedContent);
        }

        // Mark as completed
        setQueue((prev) =>
          prev.map((m) =>
            m.id === message.id
              ? {
                  ...m,
                  status: 'completed' as const,
                  response: accumulatedContent,
                  completedAt: new Date(),
                }
              : m,
          ),
        );

        setActiveStreams((prev) => {
          const newMap = new Map(prev);
          newMap.set(message.id, {
            messageId: message.id,
            content: accumulatedContent,
            isStreaming: false,
          });
          return newMap;
        });

        onMessageComplete?.(message.id, accumulatedContent);

        // Invalidate queries to refresh file list
        queryClient.invalidateQueries({ queryKey: ['files', 'list', sessionId] });
        queryClient.invalidateQueries({ queryKey: ['ai', 'history', sessionId] });
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          // User cancelled - mark as failed
          setQueue((prev) =>
            prev.map((m) =>
              m.id === message.id
                ? { ...m, status: 'failed' as const, error: 'Cancelled by user' }
                : m,
            ),
          );
        } else {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          setQueue((prev) =>
            prev.map((m) =>
              m.id === message.id ? { ...m, status: 'failed' as const, error: errorMessage } : m,
            ),
          );
          onMessageError?.(message.id, errorMessage);
        }

        setActiveStreams((prev) => {
          const newMap = new Map(prev);
          const existing = newMap.get(message.id);
          if (existing) {
            newMap.set(message.id, { ...existing, isStreaming: false });
          }
          return newMap;
        });
      } finally {
        processingRef.current.delete(message.id);
        abortControllersRef.current.delete(message.id);
      }
    },
    [
      sessionId,
      selectedModel,
      autoExecute,
      mode,
      autonomousMode,
      queryClient,
      onStreamUpdate,
      onMessageComplete,
      onMessageError,
    ],
  );

  // Process queue - pick up pending messages when slots are available
  useEffect(() => {
    const pendingMessages = queue.filter((m) => m.status === 'pending');
    const currentlyProcessing = processingRef.current.size;
    const slotsAvailable = maxConcurrent - currentlyProcessing;

    if (slotsAvailable > 0 && pendingMessages.length > 0) {
      // Process next pending messages up to available slots
      const toProcess = pendingMessages.slice(0, slotsAvailable);
      toProcess.forEach((msg) => {
        processMessage(msg);
      });
    }
  }, [queue, maxConcurrent, processMessage]);

  // Stop a specific message
  const stopMessage = useCallback((messageId: string) => {
    const controller = abortControllersRef.current.get(messageId);
    if (controller) {
      controller.abort();
    }
  }, []);

  // Stop all active messages
  const stopAll = useCallback(() => {
    abortControllersRef.current.forEach((controller) => {
      controller.abort();
    });
  }, []);

  // Clear completed/failed messages from queue
  const clearCompleted = useCallback(() => {
    setQueue((prev) => prev.filter((m) => m.status === 'pending' || m.status === 'processing'));
    setActiveStreams((prev) => {
      const newMap = new Map();
      prev.forEach((stream, id) => {
        if (stream.isStreaming) {
          newMap.set(id, stream);
        }
      });
      return newMap;
    });
  }, []);

  // Get streaming content for a specific message
  const getStreamingContent = useCallback(
    (messageId: string): string | undefined => {
      return activeStreams.get(messageId)?.content;
    },
    [activeStreams],
  );

  // Check if a specific message is streaming
  const isMessageStreaming = useCallback(
    (messageId: string): boolean => {
      return activeStreams.get(messageId)?.isStreaming ?? false;
    },
    [activeStreams],
  );

  return {
    // Queue state
    queue,
    pendingCount,
    processingCount,
    hasActiveWork,
    activeStreams,

    // Actions
    enqueueMessage,
    stopMessage,
    stopAll,
    clearCompleted,

    // Helpers
    getStreamingContent,
    isMessageStreaming,
  };
}
